package or.garden.manager.domain

import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.forms.formData
import io.ktor.client.request.forms.submitFormWithBinaryData
import io.ktor.client.request.get
import io.ktor.client.request.headers
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.isSuccess
import karika.distribucija.ba.domain.model.ResultState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.launch
import or.garden.manager.domain.GardenHttpClient.GARDEN_CLIENT
import or.garden.manager.domain.model.ApiResponse
import or.garden.manager.domain.model.SeedData
import or.garden.manager.domain.request.CreateSeedRequest

internal class SeedApi {
    suspend fun getSeeds(): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.get("${GardenHttpClient.baseUrl}/my-seeds")
    }

    suspend fun createSeed(request: CreateSeedRequest): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.post("${GardenHttpClient.baseUrl}/my-seeds") {
            setBody(request)
        }
    }

    // New: Create seed with multipart (for binary image upload)
    suspend fun createSeedMultipart(
        name: String,
        latinName: String,
        hybrid: Boolean,
        photoBytes: ByteArray?,
        backPhotoBytes: ByteArray?,
        type: String? = null,
        harvestTime: Int? = null,
        seedlingDate: String? = null
    ): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.submitFormWithBinaryData(
            url = "${GardenHttpClient.baseUrl}/my-seeds",
            formData = formData {
                append("name", name)
                append("latinName", latinName)
                append("hybrid", hybrid.toString())
                type?.let { append("type", it) }
                harvestTime?.let { append("harvestTime", it.toString()) }
                seedlingDate?.let { append("seedlingDate", it) }

                photoBytes?.let {
                    append("photo", it, Headers.build {
                        append(HttpHeaders.ContentType, "image/jpeg")
                        append(HttpHeaders.ContentDisposition, "filename=photo.jpg")
                    })
                }

                backPhotoBytes?.let {
                    append("backPhoto", it, Headers.build {
                        append(HttpHeaders.ContentType, "image/jpeg")
                        append(HttpHeaders.ContentDisposition, "filename=back.jpg")
                    })
                }
            }
        ) {
            headers {
                append(HttpHeaders.Accept, "application/json")
            }
        }
    }

    suspend fun updateSeed(id: Int, request: CreateSeedRequest): Result<HttpResponse> =
        runCatching {
            return@runCatching GARDEN_CLIENT.put("${GardenHttpClient.baseUrl}/my-seeds/$id") {
                setBody(request)
            }
        }

    // New: Update seed with multipart (for binary image upload)
    suspend fun updateSeedMultipart(
        id: Int,
        name: String,
        latinName: String,
        hybrid: Boolean,
        photoBytes: ByteArray? = null,
        backPhotoBytes: ByteArray? = null,
        type: String? = null,
        harvestTime: Int? = null,
        seedlingDate: String? = null
    ): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.submitFormWithBinaryData(
            url = "${GardenHttpClient.baseUrl}/my-seeds/$id",
            formData = formData {
                append("name", name)
                append("latinName", latinName)
                append("hybrid", hybrid.toString())
                type?.let { append("type", it) }
                harvestTime?.let { append("harvestTime", it.toString()) }
                seedlingDate?.let { append("seedlingDate", it) }

                photoBytes?.let {
                    append("photo", it, Headers.build {
                        append(HttpHeaders.ContentType, "image/jpeg")
                        append(HttpHeaders.ContentDisposition, "filename=photo.jpg")
                    })
                }

                backPhotoBytes?.let {
                    append("backPhoto", it, Headers.build {
                        append(HttpHeaders.ContentType, "image/jpeg")
                        append(HttpHeaders.ContentDisposition, "filename=back.jpg")
                    })
                }
            }
        ) {
            method = HttpMethod.Put
            headers {
                append(HttpHeaders.Accept, "application/json")
            }
        }
    }

    suspend fun deleteSeed(id: Int): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.delete("${GardenHttpClient.baseUrl}/my-seeds/$id")
    }
}

private data object SeedRepository {
    fun getSeeds(): Flow<ResultState<ApiResponse<List<SeedData>>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = SeedApi()
                .getSeeds()
                .getOrNull()

            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to load seeds. Please try again!"))
        }
    }

    fun createSeed(request: CreateSeedRequest): Flow<ResultState<ApiResponse<SeedData>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = SeedApi()
                .createSeed(request)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to create seed: ${e.message}"))
        }
    }

    // New: Create seed with multipart
    fun createSeedMultipart(
        name: String,
        latinName: String,
        hybrid: Boolean,
        photoBytes: ByteArray?,
        backPhotoBytes: ByteArray?,
        type: String? = null,
        harvestTime: Int? = null,
        seedlingDate: String? = null
    ): Flow<ResultState<ApiResponse<SeedData>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = SeedApi()
                .createSeedMultipart(
                    name,
                    latinName,
                    hybrid,
                    photoBytes,
                    backPhotoBytes,
                    type,
                    harvestTime,
                    seedlingDate
                )
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to create seed: ${e.message}"))
        }
    }

    fun updateSeed(id: Int, request: CreateSeedRequest): Flow<ResultState<ApiResponse<SeedData>>> =
        flow {
            emit(ResultState.Loading)
            delay(GardenHttpClient.apiDelayMillis)
            try {
                val response = SeedApi()
                    .updateSeed(id, request)
                    .getOrNull()
                if (response != null && response.status.isSuccess()) {
                    emit(ResultState.Success(response.body()))
                } else {
                    emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
                }
            } catch (e: Exception) {
                e.printStackTrace()
                emit(ResultState.Error("Failed to update seed: ${e.message}"))
            }
        }

    // New: Update seed with multipart
    fun updateSeedMultipart(
        id: Int,
        name: String,
        latinName: String,
        hybrid: Boolean,
        photoBytes: ByteArray? = null,
        backPhotoBytes: ByteArray? = null,
        type: String? = null,
        harvestTime: Int? = null,
        seedlingDate: String? = null
    ): Flow<ResultState<ApiResponse<SeedData>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = SeedApi()
                .updateSeedMultipart(
                    id,
                    name,
                    latinName,
                    hybrid,
                    photoBytes,
                    backPhotoBytes,
                    type,
                    harvestTime,
                    seedlingDate
                )
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to update seed: ${e.message}"))
        }
    }

    fun deleteSeed(id: Int): Flow<ResultState<ApiResponse<Nothing>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = SeedApi()
                .deleteSeed(id)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to delete seed: ${e.message}"))
        }
    }
}

data object SeedService {
    fun getSeeds(result: (ResultState<ApiResponse<List<SeedData>>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            SeedRepository.getSeeds().collect { result(it) }
        }
    }

    fun createSeed(
        request: CreateSeedRequest,
        result: (ResultState<ApiResponse<SeedData>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            SeedRepository.createSeed(request).collect { result(it) }
        }
    }

    // New: Create seed with multipart (for large images)
    fun createSeedMultipart(
        name: String,
        latinName: String,
        hybrid: Boolean,
        photoBytes: ByteArray?,
        backPhotoBytes: ByteArray?,
        type: String? = null,
        harvestTime: Int? = null,
        seedlingDate: String? = null,
        result: (ResultState<ApiResponse<SeedData>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            SeedRepository.createSeedMultipart(
                name, latinName, hybrid, photoBytes, backPhotoBytes, type, harvestTime, seedlingDate
            ).collect { result(it) }
        }
    }

    fun updateSeed(
        id: Int,
        request: CreateSeedRequest,
        result: (ResultState<ApiResponse<SeedData>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            SeedRepository.updateSeed(id, request).collect { result(it) }
        }
    }

    // New: Update seed with multipart (for large images)
    fun updateSeedMultipart(
        id: Int,
        name: String,
        latinName: String,
        hybrid: Boolean,
        photoBytes: ByteArray? = null,
        backPhotoBytes: ByteArray? = null,
        type: String? = null,
        harvestTime: Int? = null,
        seedlingDate: String? = null,
        result: (ResultState<ApiResponse<SeedData>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            SeedRepository.updateSeedMultipart(
                id,
                name,
                latinName,
                hybrid,
                photoBytes,
                backPhotoBytes,
                type,
                harvestTime,
                seedlingDate
            ).collect { result(it) }
        }
    }

    fun deleteSeed(id: Int, result: (ResultState<ApiResponse<Nothing>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            SeedRepository.deleteSeed(id).collect { result(it) }
        }
    }
}
